import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Save, Loader2, Upload, X, Plus, ArrowUp, ArrowDown, Trash2, Play } from "lucide-react";
import { useAuth, formatApiError } from "@/context/AuthContext";
import { useContent } from "@/context/ContentContext";
import { inputCls, labelCls, btnPrimary, PanelHeader } from "./_shared";
import { fileToDataUrl } from "./GenericListPanel";
import { detectVideoKind } from "@/lib/media";

const EMPTY_SLIDE = { image_url: "", image_data: null };

export default function HeroPanel() {
  const { adminApi } = useAuth();
  const { refresh } = useContent();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    adminApi
      .get("/content")
      .then((r) => {
        const h = { ...r.data.hero };
        h.slides = Array.isArray(h.slides) ? h.slides : [];
        h.video_url = h.video_url || "";
        // If legacy image_url/image_data exists and no slides, seed one slide
        if (h.slides.length === 0 && (h.image_url || h.image_data)) {
          h.slides = [{ image_url: h.image_url || "", image_data: h.image_data || null }];
        }
        setForm(h);
      })
      .catch(() => toast.error("Failed to load"));
  }, [adminApi]);

  if (!form) return <div className="text-white/50">Loading…</div>;

  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setStat = (i, k, v) =>
    setForm((f) => ({ ...f, stats: f.stats.map((s, idx) => (idx === i ? { ...s, [k]: v } : s)) }));

  const updateSlide = (i, updater) =>
    setForm((f) => ({ ...f, slides: f.slides.map((s, idx) => (idx === i ? updater(s) : s)) }));

  const addSlide = () => setForm((f) => ({ ...f, slides: [...(f.slides || []), { ...EMPTY_SLIDE }] }));

  const removeSlide = (i) => setForm((f) => ({ ...f, slides: f.slides.filter((_, idx) => idx !== i) }));

  const moveSlide = (i, dir) => {
    setForm((f) => {
      const arr = [...f.slides];
      const j = i + dir;
      if (j < 0 || j >= arr.length) return f;
      [arr[i], arr[j]] = [arr[j], arr[i]];
      return { ...f, slides: arr };
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const firstSlide = form.slides?.[0] || {};
      await adminApi.put("/content/hero", {
        eyebrow: form.eyebrow,
        heading_1: form.heading_1,
        heading_2: form.heading_2,
        heading_3: form.heading_3 || "",
        subtitle: form.subtitle,
        stats: form.stats,
        slides: form.slides || [],
        video_url: (form.video_url || "").trim(),
        // Keep legacy fields in sync with slide[0] for backward-compat readers
        image_url: firstSlide.image_url || "",
        image_data: firstSlide.image_data || null,
      });
      toast.success("Hero section updated");
      refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const videoKind = form.video_url ? detectVideoKind(form.video_url) : null;

  return (
    <div data-testid="hero-panel">
      <PanelHeader
        eyebrow="01"
        title="Hero Section"
        description="Top of the homepage — supports a looping video OR a slideshow of images."
        actions={
          <button data-testid="hero-save" onClick={save} disabled={saving} className={btnPrimary}>
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save
          </button>
        }
      />

      {/* VIDEO BACKGROUND */}
      <div className="mb-8 border border-white/10 bg-[#1A1D21] p-6">
        <div className="flex items-center gap-2 mb-3">
          <Play size={14} className="text-[#FF6B00]" />
          <div className="font-display uppercase tracking-widest text-sm">Background Video (optional)</div>
        </div>
        <p className="text-xs text-white/50 mb-4 leading-relaxed">
          Paste a direct MP4/WebM URL for the best looping-silent-background effect, OR a YouTube URL for a hosted video. When set, this overrides the image slideshow.
        </p>
        <input
          data-testid="hero-video-url"
          className={inputCls}
          placeholder="https://…/video.mp4  or  https://youtube.com/watch?v=…"
          value={form.video_url || ""}
          onChange={(e) => setF("video_url", e.target.value)}
        />
        {form.video_url && (
          <div className="mt-3 text-xs flex items-center gap-2">
            {videoKind === "mp4" && <span className="text-green-400">✓ Direct video — will autoplay muted &amp; loop as hero background</span>}
            {videoKind === "youtube" && <span className="text-green-400">✓ YouTube — will embed as a muted looping background</span>}
            {!videoKind && <span className="text-yellow-400">⚠ URL not recognized — must be an MP4, WebM or YouTube link</span>}
          </div>
        )}
      </div>

      {/* SLIDES */}
      <div className="mb-8 border border-white/10 bg-[#1A1D21] p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="font-display uppercase tracking-widest text-sm">Image Slideshow</div>
            <div className="text-xs text-white/50 mt-1">
              {form.video_url && detectVideoKind(form.video_url)
                ? "Ignored while a background video is set."
                : "Add 2+ slides to auto-cycle every 6 seconds. Add 1 slide for a static background."}
            </div>
          </div>
          <button
            type="button"
            onClick={addSlide}
            data-testid="hero-add-slide"
            className="inline-flex items-center gap-2 border border-white/20 hover:border-[#FF6B00] hover:text-[#FF6B00] px-4 py-2 text-xs font-display tracking-widest uppercase transition-colors"
          >
            <Plus size={12} /> Add Slide
          </button>
        </div>

        <div className="space-y-3">
          {(form.slides || []).length === 0 && (
            <div className="text-white/40 text-sm">No slides yet — click "Add Slide" or set a background video.</div>
          )}
          {(form.slides || []).map((s, i) => (
            <SlideRow
              key={i}
              slide={s}
              index={i}
              total={form.slides.length}
              onChange={(u) => updateSlide(i, u)}
              onRemove={() => removeSlide(i)}
              onMoveUp={() => moveSlide(i, -1)}
              onMoveDown={() => moveSlide(i, 1)}
            />
          ))}
        </div>
      </div>

      {/* Text fields */}
      <div className="space-y-6">
        <div>
          <label className={labelCls}>Eyebrow (small tag above heading)</label>
          <input className={inputCls} value={form.eyebrow || ""} onChange={(e) => setF("eyebrow", e.target.value)} />
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          <div>
            <label className={labelCls}>Heading Line 1</label>
            <input className={inputCls} value={form.heading_1 || ""} onChange={(e) => setF("heading_1", e.target.value)} />
          </div>
          <div>
            <label className={labelCls}>Heading Line 2 (orange)</label>
            <input className={inputCls} value={form.heading_2 || ""} onChange={(e) => setF("heading_2", e.target.value)} />
          </div>
          <div>
            <label className={labelCls}>Heading Line 3 (optional)</label>
            <input
              data-testid="hero-heading-3"
              className={inputCls}
              value={form.heading_3 || ""}
              onChange={(e) => setF("heading_3", e.target.value)}
              placeholder="Leave blank for two-line hero"
            />
          </div>
        </div>
        <div>
          <label className={labelCls}>Subtitle Paragraph</label>
          <textarea className={`${inputCls} min-h-[120px]`} value={form.subtitle || ""} onChange={(e) => setF("subtitle", e.target.value)} />
        </div>
        <div>
          <label className={labelCls}>Stat Cards (3 numbers below the CTA buttons)</label>
          <div className="grid md:grid-cols-3 gap-4">
            {(form.stats || []).map((s, i) => (
              <div key={i} className="border border-white/10 bg-[#1A1D21] p-4 space-y-3">
                <input className={inputCls} placeholder="Number (e.g. 8+)" value={s.n || ""} onChange={(e) => setStat(i, "n", e.target.value)} />
                <input className={inputCls} placeholder="Label" value={s.l || ""} onChange={(e) => setStat(i, "l", e.target.value)} />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function SlideRow({ slide, index, total, onChange, onRemove, onMoveUp, onMoveDown }) {
  const fileRef = useRef();
  const [processing, setProcessing] = useState(false);
  const preview = slide.image_data || slide.image_url;

  const handleFile = async () => {
    const f = fileRef.current?.files?.[0];
    if (!f) return;
    setProcessing(true);
    try {
      const dataUrl = await fileToDataUrl(f, 2400, 0.85);
      onChange((s) => ({ ...s, image_data: dataUrl, image_url: "" }));
    } catch (e) {
      toast.error(e.message || "Image upload failed");
    } finally {
      setProcessing(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div data-testid={`hero-slide-${index}`} className="border border-white/10 bg-[#121212] p-4 flex flex-col md:flex-row gap-4">
      <div className="flex md:flex-col gap-1 shrink-0 text-white/40 justify-center items-center md:justify-start md:w-6">
        <button onClick={onMoveUp} disabled={index === 0} className="hover:text-[#FF6B00] disabled:opacity-30" aria-label="Move up">
          <ArrowUp size={14} />
        </button>
        <div className="font-display text-xs">{index + 1}</div>
        <button onClick={onMoveDown} disabled={index === total - 1} className="hover:text-[#FF6B00] disabled:opacity-30" aria-label="Move down">
          <ArrowDown size={14} />
        </button>
      </div>

      <div className="w-32 h-24 shrink-0 bg-[#1A1D21] border border-white/10 overflow-hidden">
        {preview ? (
          <img src={preview} alt="" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-white/30 text-xs">Empty</div>
        )}
      </div>

      <div className="flex-1 space-y-2 min-w-0">
        <input
          ref={fileRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif"
          data-testid={`hero-slide-${index}-file`}
          onChange={handleFile}
          disabled={processing}
          className="block w-full text-xs text-white/70 file:mr-3 file:py-1.5 file:px-3 file:border-0 file:bg-[#FF6B00] file:text-white file:font-display file:uppercase file:tracking-widest file:text-[10px] hover:file:bg-[#E65A00] cursor-pointer disabled:opacity-50"
        />
        {processing && (
          <div className="text-xs text-[#FF6B00] flex items-center gap-1">
            <Loader2 size={10} className="animate-spin" /> Processing…
          </div>
        )}
        <div className="flex items-center gap-2 text-xs text-white/40">
          <Upload size={10} /> Or paste URL:
        </div>
        <input
          className="w-full bg-[#0d0d0d] border border-white/15 rounded-none px-3 py-2 text-sm text-white placeholder:text-white/40 focus:outline-none focus:border-[#FF6B00]"
          data-testid={`hero-slide-${index}-url`}
          placeholder="https://…"
          value={slide.image_url || ""}
          onChange={(e) => onChange((s) => ({ ...s, image_url: e.target.value, image_data: e.target.value ? null : s.image_data }))}
        />
      </div>

      <button
        type="button"
        onClick={onRemove}
        data-testid={`hero-slide-${index}-remove`}
        aria-label="Remove slide"
        className="shrink-0 self-start md:self-center inline-flex items-center gap-1 border border-red-500/40 text-red-400 hover:bg-red-500 hover:text-white px-3 py-2 text-xs font-display tracking-widest uppercase transition-colors"
      >
        <Trash2 size={12} /> Remove
      </button>
    </div>
  );
}
